/**
 * Left slide-out navigation drawer: backdrop, nested panels, OverlayManager integration.
 */
(() => {
  if (customElements.get('ge-menu-drawer')) return;

  class GeMenuDrawer extends HTMLElement {
    connectedCallback() {
      this.backdrop = document.querySelector('[data-ge-menu-drawer-backdrop]');
      this.toggleWrap = document.querySelector('.mobile-toggle-wrapper');
      this.trigger = document.querySelector('[data-ge-menu-drawer-trigger]');
      this.stackEl = this.querySelector('[data-ge-menu-stack]');
      this.closeBtn = this.querySelector('[data-ge-menu-drawer-close]');
      this._panelStack = ['root'];

      this.trigger?.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggle();
      });
      this.closeBtn?.addEventListener('click', () => this.close());
      this.backdrop?.addEventListener('click', () => this.close());

      this.querySelectorAll('[data-ge-menu-forward]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const id = btn.getAttribute('data-ge-menu-forward');
          if (id) this.openSubpanel(id);
        });
      });

      this.querySelectorAll('[data-ge-menu-back]').forEach((btn) => {
        btn.addEventListener('click', () => this.backSubpanel());
      });

      document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape' || !this.isOpen()) return;
        if (this._panelStack.length > 1) {
          e.preventDefault();
          e.stopPropagation();
          this.backSubpanel();
          return;
        }
        this.close();
      });

      document.addEventListener('panel:closed', (e) => {
        if (e.detail?.panel === this) this.resetPanels();
      });
    }

    isOpen() {
      return typeof OverlayManager !== 'undefined' && OverlayManager.isOpen(this);
    }

    toggle() {
      if (this.isOpen()) this.close();
      else this.open();
    }

    open() {
      if (typeof OverlayManager === 'undefined') return;
      this.resetPanels();
      OverlayManager.open(this);
      this.backdrop?.removeAttribute('hidden');
      this.backdrop?.classList.add('is-open');
      document.body.classList.add('ge-menu-drawer-open');
      this.trigger?.setAttribute('aria-expanded', 'true');
      this.trigger?.classList.add('is-active');
      this.toggleWrap?.setAttribute('open', '');
      this.toggleWrap?.classList.add('active');
      this._play(this.querySelector('[data-ge-menu-panel="root"]'));
      this.closeBtn?.focus({ preventScroll: true });
    }

    close() {
      if (typeof OverlayManager === 'undefined') return;
      OverlayManager.close(this);
      this.syncClosedUi();
      this.resetPanels();
    }

    syncClosedUi() {
      const toggleWrap = document.querySelector('.mobile-toggle-wrapper');
      const trigger = document.querySelector('[data-ge-menu-drawer-trigger]');
      this.backdrop?.setAttribute('hidden', '');
      this.backdrop?.classList.remove('is-open');
      document.body.classList.remove('ge-menu-drawer-open');
      document.body.classList.remove('overflow-hidden');
      trigger?.setAttribute('aria-expanded', 'false');
      trigger?.classList.remove('is-active');
      toggleWrap?.removeAttribute('open');
      toggleWrap?.classList.remove('active');
    }

    _play(panel) {
      if (!panel) return;
      this.querySelectorAll('[data-ge-menu-panel].is-current').forEach((el) => el.classList.remove('is-current'));
      void panel.offsetWidth;
      panel.classList.add('is-current');
    }

    _setDepth() {
      this.stackEl.style.setProperty('--ge-menu-depth', String(this._panelStack.length - 1));
    }

    openSubpanel(id) {
      const panel = this.querySelector(`[data-ge-menu-panel="${id}"]`);
      if (!panel || !this.stackEl) return;
      clearTimeout(this._hideT);
      this.querySelectorAll('[data-ge-menu-panel]:not([data-ge-menu-panel="root"])').forEach((el) => {
        if (el !== panel) el.setAttribute('hidden', '');
      });
      panel.removeAttribute('hidden');
      panel.querySelector('.ge-rd__scroll')?.scrollTo(0, 0);
      this._panelStack.push(id);
      requestAnimationFrame(() => {
        this._setDepth();
        this._play(panel);
      });
      setTimeout(() => panel.querySelector('[data-ge-menu-back]')?.focus({ preventScroll: true }), 320);
    }

    backSubpanel() {
      if (!this.stackEl || this._panelStack.length <= 1) return;
      const closingId = this._panelStack.pop();
      const panel = this.querySelector(`[data-ge-menu-panel="${closingId}"]`);
      this._setDepth();
      const parentId = this._panelStack[this._panelStack.length - 1];
      this._play(this.querySelector(`[data-ge-menu-panel="${parentId}"]`));
      clearTimeout(this._hideT);
      this._hideT = setTimeout(() => panel?.setAttribute('hidden', ''), 340);
      this.querySelector(`[data-ge-menu-forward="${closingId}"]`)?.focus({ preventScroll: true });
    }

    resetPanels() {
      this._panelStack = ['root'];
      if (this.stackEl) this.stackEl.style.setProperty('--ge-menu-depth', '0');
      this.querySelectorAll('[data-ge-menu-panel]:not([data-ge-menu-panel="root"])').forEach((el) => {
        el.setAttribute('hidden', '');
        el.querySelectorAll('details[open]').forEach((d) => d.removeAttribute('open'));
      });
      this.querySelectorAll('[data-ge-menu-panel].is-current').forEach((el) => el.classList.remove('is-current'));
    }
  }

  customElements.define('ge-menu-drawer', GeMenuDrawer);

  document.addEventListener('panel:closed', (e) => {
    const panel = e.detail?.panel;
    if (!panel || panel.tagName !== 'GE-MENU-DRAWER') return;
    panel.syncClosedUi?.();
  });
})();
